import { Modal, Icon, Button } from '@distribusion/owl-frontend-ui'
import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import {
  DeleteAccountErrorReason,
  DeleteAccountType,
  DELETE_ACCOUNT_TYPES,
  DELETE_ACCOUNT_ERROR_REASONS,
} from '@pages/Account/hooks/useDeleteAccount'
import { ModalType, MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'

import '@pages/Account/Settings/Modals/ErrorModal/index.scss'

interface ErrorModalProps {
  isOpen: boolean
  onClose: () => void
  onRetry?: () => void
  errorType: ModalType | DeleteAccountType | null
  deleteErrorReason?: DeleteAccountErrorReason | null
}

interface ModalContent {
  icon: React.ReactNode
  title: string
  description: string
  buttons: React.ReactNode
}

export const ErrorModal = ({
  isOpen,
  onClose,
  onRetry,
  errorType = MODAL_TYPES.CHANGE_EMAIL,
  deleteErrorReason = DELETE_ACCOUNT_ERROR_REASONS.DEFAULT,
}: ErrorModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleNavigateToSubscription = () => {
    onClose()
    navigate(`/account/subscriptions?${searchParams}`)
  }

  const handleNavigateToPayment = () => {
    onClose()
    navigate(`/account/payment?${searchParams}`)
  }

  const getModalContent = (): ModalContent => {
    // Handle delete-account errors
    if (errorType === DELETE_ACCOUNT_TYPES.DELETE_ACCOUNT) {
      interface DeleteAccountErrorConfig {
        titleKey: string
        descriptionKey: string
        isWarning: boolean
        button: {
          onClick: () => void
          dataTag: string
          textKey: string
        }
      }

      const deleteAccountErrorConfigs: Record<DeleteAccountErrorReason, DeleteAccountErrorConfig> = {
        [DELETE_ACCOUNT_ERROR_REASONS.CANCELLATION_REQUIRED]: {
          titleKey: 'account.settings.delete.errorModal.cancellationRequired.title',
          descriptionKey: 'account.settings.delete.errorModal.cancellationRequired.description',
          isWarning: true,
          button: {
            onClick: handleNavigateToSubscription,
            dataTag: 'navigate-to-subscriptions',
            textKey: 'account.settings.delete.errorModal.cancellationRequired.button',
          },
        },
        [DELETE_ACCOUNT_ERROR_REASONS.PAYMENT_REQUIRED]: {
          titleKey: 'account.settings.delete.errorModal.paymentRequired.title',
          descriptionKey: 'account.settings.delete.errorModal.paymentRequired.description',
          isWarning: true,
          button: {
            onClick: handleNavigateToPayment,
            dataTag: 'navigate-to-payment',
            textKey: 'account.settings.delete.errorModal.paymentRequired.button',
          },
        },
        [DELETE_ACCOUNT_ERROR_REASONS.DEFAULT]: {
          titleKey: 'account.settings.delete.errorModal.default.title',
          descriptionKey: 'account.settings.delete.errorModal.default.description',
          isWarning: false,
          button: {
            onClick: onClose,
            dataTag: 'close-error-modal',
            textKey: 'account.settings.delete.errorModal.default.button',
          },
        },
      }

      const config = deleteAccountErrorConfigs[deleteErrorReason || DELETE_ACCOUNT_ERROR_REASONS.DEFAULT]
      const icon = (
        <div className={bem('icon-wrapper', config.isWarning ? 'warning' : 'error')}>
          <Icon name={config.isWarning ? 'alert' : 'cross'} size="large" />
        </div>
      )

      return {
        icon,
        title: t(config.titleKey),
        description: t(config.descriptionKey),
        buttons: (
          <Button
            onClick={config.button.onClick}
            color="primary"
            className={bem('error', 'modal-button')}
            dataTag={config.button.dataTag}
          >
            {t(config.button.textKey)}
          </Button>
        ),
      }
    }

    const defaultIcon = (
      <div className={bem('icon-wrapper', 'error')}>
        <Icon name="cross" size="large" />
      </div>
    )

    // Handle reset-password errors
    if (errorType === MODAL_TYPES.RESET_PASSWORD) {
      return {
        icon: defaultIcon,
        title: t('account.passwordRecovery.errorModal.title'),
        description: t('account.passwordRecovery.errorModal.description'),
        buttons: (
          <Button onClick={onRetry} color="primary" className={bem('error', 'modal-button')} dataTag="retry-action">
            {t('account.passwordRecovery.errorModal.button')}
          </Button>
        ),
      }
    }

    if (errorType === MODAL_TYPES.NO_USER) {
      return {
        icon: defaultIcon,
        title: t('account.passwordRecovery.noUserErrorModal.title'),
        description: t('account.passwordRecovery.noUserErrorModal.description'),
        buttons: (
          <Button onClick={onRetry} color="primary" className={bem('error', 'modal-button')} dataTag="retry-action">
            {t('account.passwordRecovery.noUserErrorModal.button')}
          </Button>
        ),
      }
    }

    // Handle change-email and change-password errors
    return {
      icon: defaultIcon,
      title:
        errorType === MODAL_TYPES.CHANGE_EMAIL
          ? t('account.settings.signIn.errorModal.title.emailError')
          : t('account.settings.signIn.errorModal.title.passwordError'),
      description: t('account.settings.signIn.errorModal.description'),
      buttons: (
        <>
          <Button
            onClick={onClose}
            variant="outline"
            className={bem('error', 'modal-button')}
            dataTag="close-error-modal"
          >
            {t('account.settings.signIn.errorModal.backButton')}
          </Button>
          <Button onClick={onRetry} color="primary" className={bem('error', 'modal-button')} dataTag="retry-action">
            {t('account.settings.signIn.errorModal.againButton')}
          </Button>
        </>
      ),
    }
  }

  const content = getModalContent()

  return (
    <Modal
      className={bem('error', 'modal')}
      dataTag="error-modal"
      maxWidth="md"
      opened={isOpen}
      onClose={onClose}
      title={
        <div className="modal-title">
          {content.icon}
          <span>{content.title}</span>
        </div>
      }
    >
      <div className="modal-content">
        <span className={bem('error', 'modal-message')}>{content.description}</span>
        <div className={bem('modal-content', 'buttons')}>{content.buttons}</div>
      </div>
    </Modal>
  )
}
